// src/components/AddDealForm.jsx
import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import { getFirstEmail, getAllEmails } from '../../utils/emailHelpers';

const AddDealForm = ({ isOpen, onClose, onSuccess, editDeal = null }) => {
  const API = useContext(ApiContext);
  const [loading, setLoading] = useState(false);
  const [individuals, setIndividuals] = useState([]);
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  
  // ✅ États pour la recherche d'individuals
  const [individualSearch, setIndividualSearch] = useState('');
  const [filteredIndividuals, setFilteredIndividuals] = useState([]);
  const [showIndividualDropdown, setShowIndividualDropdown] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState(null);
  
  const [formData, setFormData] = useState({
    dealDate: new Date().toISOString().split('T')[0],
    companyRate: '',
    collaboratorRate: '',
    annualSalary: '',
    recruiterId: '',
    individualId: '',
    clientId: '',
    positionId: '',
    contractStartDate: '',
    contractType: 'CDI',
    contractDuration: { value: '', unit: 'mois' },
    notes: '',
    status: 'en_cours'
  });

  const [calculatedAmounts, setCalculatedAmounts] = useState({
    generatedRevenue: 0,
    recruiterBonus: 0
  });

  // Charger les données de référence
  useEffect(() => {
    if (isOpen) {
      fetchReferenceData();
      if (editDeal) {
        populateFormWithDeal(editDeal);
      }
    }
  }, [isOpen, editDeal]);

  // ✅ Effet pour filtrer les individuals selon la recherche
  useEffect(() => {
    if (individualSearch.trim().length >= 2) {
      const filtered = individuals.filter(individual =>
        individual.Name.toLowerCase().includes(individualSearch.toLowerCase()) ||
        (individual.company && individual.company.toLowerCase().includes(individualSearch.toLowerCase())) ||
        getAllEmails(individual.personalEmail).some(email => email.toLowerCase().includes(individualSearch.toLowerCase())) ||
        getAllEmails(individual.professionalEmail).some(email => email.toLowerCase().includes(individualSearch.toLowerCase()))
      );
      setFilteredIndividuals(filtered.slice(0, 10)); // Limiter à 10 résultats
      setShowIndividualDropdown(true);
    } else {
      setFilteredIndividuals([]);
      setShowIndividualDropdown(false);
    }
  }, [individualSearch, individuals]);

  // ✅ Fermer le dropdown si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.individual-search-container')) {
        setShowIndividualDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculer automatiquement les montants
  useEffect(() => {
    if (formData.annualSalary && formData.companyRate && formData.collaboratorRate) {
      const revenue = (parseFloat(formData.annualSalary) * parseFloat(formData.companyRate)) / 100;
      const bonus = (revenue * parseFloat(formData.collaboratorRate)) / 100;
      
      setCalculatedAmounts({
        generatedRevenue: revenue,
        recruiterBonus: bonus
      });
    }
  }, [formData.annualSalary, formData.companyRate, formData.collaboratorRate]);

  const fetchReferenceData = async () => {
    try {
      const [individualsRes, partnersRes, usersRes, positionsRes] = await Promise.all([
        API.get('/api/individuals'),
        API.get('/api/partners'),
        API.get('/api/user/company'),
        API.get('/api/positions')
      ]);

      setIndividuals(individualsRes.data);
      setPartners(partnersRes.data);
      setUsers(usersRes.data);
      setPositions(positionsRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
      toast.error('Erreur lors du chargement des données');
    }
  };

  const populateFormWithDeal = (deal) => {
    setFormData({
      dealDate: new Date(deal.dealDate).toISOString().split('T')[0],
      companyRate: deal.companyRate.toString(),
      collaboratorRate: deal.collaboratorRate.toString(),
      annualSalary: deal.annualSalary.toString(),
      recruiterId: deal.recruiter._id || deal.recruiter,
      individualId: deal.individual._id || deal.individual,
      clientId: deal.client._id || deal.client,
      positionId: deal.position?._id || deal.position || '',
      contractStartDate: new Date(deal.contractStartDate).toISOString().split('T')[0],
      contractType: deal.contractType,
      contractDuration: deal.contractDuration || { value: '', unit: 'mois' },
      notes: deal.notes || '',
      status: deal.status
    });

    // ✅ Pré-remplir la recherche d'individual si on édite
    if (deal.individual) {
      const individual = typeof deal.individual === 'object' ? deal.individual : 
        individuals.find(ind => ind._id === deal.individual);
      if (individual) {
        setSelectedIndividual(individual);
        setIndividualSearch(individual.Name);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContractDurationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contractDuration: {
        ...prev.contractDuration,
        [field]: value
      }
    }));
  };

  // ✅ Gestionnaires pour la recherche d'individuals
  const handleIndividualSearchChange = (e) => {
    const value = e.target.value;
    setIndividualSearch(value);
    
    // Si on vide la recherche, réinitialiser la sélection
    if (!value.trim()) {
      setSelectedIndividual(null);
      setFormData(prev => ({ ...prev, individualId: '' }));
    }
  };

  const handleIndividualSelect = (individual) => {
    setSelectedIndividual(individual);
    setIndividualSearch(individual.Name);
    setFormData(prev => ({ ...prev, individualId: individual._id }));
    setShowIndividualDropdown(false);
  };

  const clearIndividualSelection = () => {
    setSelectedIndividual(null);
    setIndividualSearch('');
    setFormData(prev => ({ ...prev, individualId: '' }));
    setShowIndividualDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation des champs requis
      const required = ['companyRate', 'collaboratorRate', 'annualSalary', 'recruiterId', 'individualId', 'clientId', 'contractStartDate', 'contractType'];
      const missing = required.filter(field => !formData[field]);
      
      if (missing.length > 0) {
        toast.error(`Champs requis manquants: ${missing.join(', ')}`);
        return;
      }

      // Préparer les données
      const submitData = {
        ...formData,
        contractDuration: formData.contractDuration.value ? formData.contractDuration : undefined
      };

      // Soumettre
      const response = editDeal 
        ? await API.put(`/api/deals/${editDeal._id}`, submitData)
        : await API.post('/api/deals', submitData);

      if (response.data.success) {
        toast.success(editDeal ? 'Deal mis à jour avec succès' : 'Deal créé avec succès');
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Error submitting deal:', error);
      toast.error('Erreur lors de la sauvegarde du deal');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      dealDate: new Date().toISOString().split('T')[0],
      companyRate: '',
      collaboratorRate: '',
      annualSalary: '',
      recruiterId: '',
      individualId: '',
      clientId: '',
      positionId: '',
      contractStartDate: '',
      contractType: 'CDI',
      contractDuration: { value: '', unit: 'mois' },
      notes: '',
      status: 'en_cours'
    });
    setCalculatedAmounts({ generatedRevenue: 0, recruiterBonus: 0 });
    
    // ✅ Réinitialiser la recherche d'individuals
    setIndividualSearch('');
    setSelectedIndividual(null);
    setFilteredIndividuals([]);
    setShowIndividualDropdown(false);
    
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <i className="bi bi-currency-dollar text-white"></i>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white m-0">
                  {editDeal ? 'Modifier le Deal' : 'Nouveau Deal'}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              type="button"
              className="text-white/80 hover:text-white transition-colors"
            >
              <i className="bi bi-x-lg text-xl"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informations générales */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-800 border-b pb-2">
                Informations générales
              </h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Date du deal *
                </label>
                <input
                  type="date"
                  name="dealDate"
                  value={formData.dealDate}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Candidat recruté *
                </label>
                
                {/* ✅ Barre de recherche avec autocomplétion */}
                <div className="individual-search-container relative">
                  <div className="flex">
                    <input
                      type="text"
                      value={individualSearch}
                      onChange={handleIndividualSearchChange}
                      onFocus={() => {
                        if (filteredIndividuals.length > 0) {
                          setShowIndividualDropdown(true);
                        }
                      }}
                      placeholder="Rechercher un candidat par nom, entreprise..."
                      className={`w-full border rounded-l px-3 py-2 focus:ring-2 focus:ring-indigo-500 ${
                        selectedIndividual ? 'border-green-300 bg-green-50' : 'border-slate-300'
                      }`}
                      required
                    />
                    
                    {/* Bouton pour effacer la sélection */}
                    {selectedIndividual && (
                      <button
                        type="button"
                        onClick={clearIndividualSelection}
                        className="px-3 py-2 bg-gray-100 border border-l-0 border-slate-300 rounded-r hover:bg-gray-200 text-slate-600"
                        title="Effacer la sélection"
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>

                  {/* Dropdown avec les résultats */}
                  {showIndividualDropdown && filteredIndividuals.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredIndividuals.map((individual) => (
                        <div
                          key={individual._id}
                          onClick={() => handleIndividualSelect(individual)}
                          className="px-4 py-3 hover:bg-indigo-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{individual.Name}</p>
                              <div className="flex items-center space-x-2 text-sm text-slate-600">
                                {individual.company && (
                                  <span><i className="bi bi-building mr-1"></i>{individual.company}</span>
                                )}
                                {individual.city && (
                                  <span><i className="bi bi-geo-alt mr-1"></i>{individual.city}</span>
                                )}
                              </div>
                              {(getAllEmails(individual.personalEmail).length > 0 || getAllEmails(individual.professionalEmail).length > 0) && (
                                <p className="text-xs text-slate-500">
                                  {getFirstEmail(individual.professionalEmail) || getFirstEmail(individual.personalEmail)}
                                </p>
                              )}
                            </div>
                            
                            {/* Photo de profil */}
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                              {individual.photo ? (
                                <img 
                                  src={individual.photo} 
                                  alt={individual.Name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">
                                  {individual.Name.charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Message si beaucoup de résultats */}
                      {individuals.filter(ind => 
                        ind.Name.toLowerCase().includes(individualSearch.toLowerCase())
                      ).length > 10 && (
                        <div className="px-4 py-2 text-sm text-slate-500 bg-gray-50 text-center">
                          Affichage des 10 premiers résultats. Continuez à taper pour affiner.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Message si aucun résultat */}
                  {showIndividualDropdown && individualSearch.length >= 2 && filteredIndividuals.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg p-4 text-center text-slate-500">
                      Aucun candidat trouvé pour "{individualSearch}"
                    </div>
                  )}

                  {/* Indicateur de candidat sélectionné */}
                  {selectedIndividual && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-green-800">✓ {selectedIndividual.Name}</p>
                          {selectedIndividual.company && (
                            <p className="text-sm text-green-600">{selectedIndividual.company}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={clearIndividualSelection}
                          className="text-green-600 hover:text-green-800"
                        >
                          <i className="bi bi-x-circle"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Client *
                </label>
                <select
                  name="clientId"
                  value={formData.clientId}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Sélectionner un client</option>
                  {partners.map(partner => (
                    <option key={partner._id} value={partner._id}>
                      {partner.name} - {partner.city || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Recruteur *
                </label>
                <select
                  name="recruiterId"
                  value={formData.recruiterId}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Sélectionner un recruteur</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name} - {user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Position (optionnel)
                </label>
                <select
                  name="positionId"
                  value={formData.positionId}
                  onChange={handleInputChange}
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Aucune position spécifique</option>
                  {positions.map(position => (
                    <option key={position._id} value={position._id}>
                      {position.title} - {position.partner?.name || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Informations financières */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-800 border-b pb-2">
                Informations financières
              </h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Salaire annuel brut (€) *
                </label>
                <input
                  type="number"
                  name="annualSalary"
                  value={formData.annualSalary}
                  onChange={handleInputChange}
                  min="0"
                  step="1000"
                  required
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: 50000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Taux entreprise (%) *
                </label>
                <input
                  type="number"
                  name="companyRate"
                  value={formData.companyRate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.1"
                  required
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: 20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Taux collaborateur (%) *
                </label>
                <input
                  type="number"
                  name="collaboratorRate"
                  value={formData.collaboratorRate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.1"
                  required
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: 30"
                />
              </div>

              {/* Calculs automatiques */}
              {calculatedAmounts.generatedRevenue > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <h4 className="font-medium text-slate-800">Calculs automatiques :</h4>
                  <div className="text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span>CA généré :</span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(calculatedAmounts.generatedRevenue)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Prime recruteur :</span>
                      <span className="font-medium text-purple-600">
                        {formatCurrency(calculatedAmounts.recruiterBonus)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Informations du contrat */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-800 border-b pb-2">
                Informations du contrat
              </h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Date de début de contrat *
                </label>
                <input
                  type="date"
                  name="contractStartDate"
                  value={formData.contractStartDate}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Type de contrat *
                </label>
                <select
                  name="contractType"
                  value={formData.contractType}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="CDI">CDI</option>
                  <option value="CDD">CDD</option>
                  <option value="Mission">Mission</option>
                  <option value="Freelance">Freelance</option>
                  <option value="Stage">Stage</option>
                </select>
              </div>

              {/* Durée du contrat (pour CDD/Mission) */}
              {(formData.contractType === 'CDD' || formData.contractType === 'Mission') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Durée du contrat
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      value={formData.contractDuration.value}
                      onChange={(e) => handleContractDurationChange('value', e.target.value)}
                      min="1"
                      className="flex-1 border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      placeholder="Durée"
                    />
                    <select
                      value={formData.contractDuration.unit}
                      onChange={(e) => handleContractDurationChange('unit', e.target.value)}
                      className="border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="jours">Jours</option>
                      <option value="semaines">Semaines</option>
                      <option value="mois">Mois</option>
                      <option value="années">Années</option>
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Statut
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="en_cours">En cours</option>
                  <option value="valide">Validé</option>
                  <option value="facture">Facturé</option>
                  <option value="paye">Payé</option>
                  <option value="annule">Annulé</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-800 border-b pb-2">
                Notes et commentaires
              </h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="4"
                  maxLength="1000"
                  className="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Commentaires sur le deal..."
                />
                <div className="text-xs text-slate-500 mt-1">
                  {formData.notes.length}/1000 caractères
                </div>
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sauvegarde...
                </span>
              ) : (
                editDeal ? 'Mettre à jour' : 'Créer le deal'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDealForm;